import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, ArrowRight } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Introduce email y contrasena');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bienvenido a La Finka');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciales invalidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left hero - logo showcase */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden items-center justify-center bg-zinc-950">
        {/* Decorative background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-600/3 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(245,158,11,0.03),transparent_70%)]" />
        </div>

        <div className="relative z-10 flex flex-col items-center">
          {/* Logo card with glow effect */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent rounded-3xl blur-sm opacity-70" />
            <div className="relative bg-white rounded-3xl p-10 shadow-2xl shadow-black/40">
              <img
                src="/finka.jpeg"
                alt="La Finka Logo"
                className="w-72 h-auto"
              />
            </div>
          </div>

          <div className="mt-8 text-center max-w-sm">
            <p className="text-base text-zinc-400 leading-relaxed">
              Sistema integral de gestion para tu bar
            </p>
            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-heading font-bold text-amber-500">24/7</span>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider mt-0.5">Control</span>
              </div>
              <div className="w-px h-8 bg-zinc-800" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-heading font-bold text-amber-500">100%</span>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider mt-0.5">Digital</span>
              </div>
              <div className="w-px h-8 bg-zinc-800" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-heading font-bold text-amber-500">Simple</span>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider mt-0.5">Y Rapido</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-zinc-950">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10 text-center">
            <div className="relative inline-block">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-amber-500/20 to-transparent rounded-2xl blur-sm" />
              <div className="relative bg-white rounded-2xl p-5">
                <img
                  src="/finka.jpeg"
                  alt="La Finka Logo"
                  className="w-40 h-auto mx-auto"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-4">Sistema de Gestion</p>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-zinc-50">Iniciar Sesion</h2>
            <p className="text-sm text-zinc-500 mt-1">Introduce tus credenciales para acceder</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@bar.local"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 placeholder:text-zinc-600 h-11"
                  data-testid="login-email-input"
                  aria-label="Email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300 text-sm">Contrasena</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 placeholder:text-zinc-600 h-11"
                  data-testid="login-password-input"
                  aria-label="Contrasena"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold tracking-wide active:scale-[0.98] transition-all"
              data-testid="login-submit-button"
            >
              {loading ? 'Accediendo...' : 'Acceder'}
              {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
